import React, { useState } from "react";
import { Badge, Divider, Menu, MenuItem, Stack } from "@mui/material";
import { Notifications } from "@mui/icons-material";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import dayjs from "dayjs";
import { useQuickLinksItems } from "../../../context/QuickLinksContext";
import axiosInstance from "../../../utils/config/config";
import { getAllNotifications } from "../../../redux/actions/notifications.dispatcher";

import {
  StyledIconButton,
  HeaderTitle,
  MarkAllReadText,
  StyledMenuItem,
  NotificationContent,
  menuPaperProps,
  NotificationDate,
} from "./NotificationMenu.styles";
import {
  TITLE,
  MARK_ALL_READ_TEXT,
  EMPTY_STATE_TEXT,
} from "./NotificationMenu.constants";

const NotificationMenu = () => {
  const dispatch = useDispatch();

  const { notifications } = useSelector((state) => state.notifications);
  const { userData } = useSelector((state) => state.user);
  const navigate = useNavigate();

  const [anchorEl, setAnchorEl] = useState(null);
  const { setSelectedTab, setSessionTab, setReviewPractitionerId } = useQuickLinksItems();

  const open = Boolean(anchorEl);

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const markAsRead = async (id) => {
    try {
      const response = await axiosInstance.put("/notifications/markAsRead", {
        notificationId: id,
      });

      if (response.status === 200) {
        dispatch(getAllNotifications());
      }
    } catch (error) {
      console.log(error);
    }
  };

  const markAllAsRead = async () => {
    try {
      const response = await axiosInstance.put("/notifications/markAllAsRead");

      if (response.status === 200) {
        dispatch(getAllNotifications());
      }
    } catch (error) {
      console.log(error);
    } finally {
      handleClose();
    }
  };

  const handleNotificationClick = (notif) => {
    markAsRead(notif?.id);

    // Profile-change moderation: admins go to the Practitioners admin tab,
    // practitioners go to their profile (Settings > Profile).
    if (notif?.type === "profile_edit") {
      if (userData?.role === "admin") {
        setSelectedTab("adminPractitioners");
        // notif.link carries the practitioner id to auto-open the review modal.
        if (notif?.link) setReviewPractitionerId(String(notif.link));
      } else {
        navigate("/settings");
      }
      handleClose();
      return;
    }

    // Blog moderation: admins land on the review queue, authors on their posts.
    // Without this the notification fell through to the Exchange tab and the
    // author never found the rejection reason (QA 2026-07-27).
    if (notif?.type === "blog") {
      setSelectedTab(userData?.role === "admin" ? "blogModeration" : "blog");
      handleClose();
      return;
    }

    // Route to the appropriate tab based on notification type
    if (notif?.type === "session_cancelled" || notif?.type === "new_booking" || notif?.type === "session_rescheduled") {
      setSelectedTab("patient");
    } else {
      setSelectedTab("exchange");
      setSessionTab("setupsession");
    }

    handleClose();
  };

  const unreadCount =
    notifications?.filter((notif) => !notif?.isRead)?.length ?? 0;
  const isEmpty = !notifications || notifications.length === 0;

  return (
    <>
      <StyledIconButton onClick={handleClick} color="inherit">
        <Badge badgeContent={unreadCount} color="error">
          <Notifications />
        </Badge>
      </StyledIconButton>

      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "left",
        }}
        transformOrigin={{
          vertical: "top",
          horizontal: "left",
        }}
        PaperProps={menuPaperProps}
      >
        <Stack direction="row" alignItems="center">
          <HeaderTitle>{TITLE}</HeaderTitle>
          <MarkAllReadText onClick={markAllAsRead}>
            {MARK_ALL_READ_TEXT}
          </MarkAllReadText>
        </Stack>
        <Divider />
        {isEmpty ? (
          <MenuItem>{EMPTY_STATE_TEXT}</MenuItem>
        ) : (
          notifications?.map((notif) => (
            <StyledMenuItem
              key={notif.id}
              isRead={notif?.isRead}
              onClick={() => handleNotificationClick(notif)}
            >
              <Stack>
                <NotificationContent>{notif?.title}</NotificationContent>
                <NotificationContent>{notif?.content}</NotificationContent>
                <NotificationDate variant="body2">
                  {dayjs(notif?.createdAt).format("MM/DD/YYYY")}
                </NotificationDate>
              </Stack>
            </StyledMenuItem>
          ))
        )}
      </Menu>
    </>
  );
};

export default NotificationMenu;
